import React from 'react';
import { FunctionComponent } from 'react';
import { Field } from '../../Field/Field';
import { Input, FieldContainer, InputProps } from '../../Components';
import { FieldRenderProps } from '../../Field';
import classNames from 'classnames';
import { Temporal } from 'temporal-polyfill';
import styles from './styles.module.scss';

type Props = {
    id?: string;
    name: string;
    label: string;
    description?: string;
    initialValue?: string;
};

export type DateFieldProps = Omit<
    FieldRenderProps<string>,
    'memoChild' | 'children' | 'initialValue'
> &
    Omit<InputProps, 'label' | 'name' | 'onChange' | 'onBlur' | 'value' | 'defaultValue'> &
    Props;

export const DateField: FunctionComponent<DateFieldProps> = ({
    id,
    name,
    label,
    description,
    initialValue,
    required,
    onBlurValidate,
    onChangeValidate,
    onSubmitValidate,
    className,
    ...inputProps
}) => {
    return (
        <Field<string>
            id={id}
            name={name}
            description={description}
            initialValue={initialValue}
            onBlurValidate={onBlurValidate}
            onChangeValidate={onChangeValidate}
            onSubmitValidate={onSubmitValidate}
            required={required}
        >
            {({
                fieldId,
                value,
                setValue,
                onBlur,
                isValid,
                errors,
                errorId,
                descriptionId,
                required,
                requiredStrategy,
            }) => {
                const handleOnChange = (val: string | null) => {
                    if (val === null) return setValue(null);
                    setValue(Temporal.PlainDate.from(val).toString());
                };

                const currentValue = (() => {
                    if (value === null) return null;
                    // TODO This should be removed and fieldValue used instead once everything is a date not a timestamp
                    if (typeof value === 'number') {
                        return Temporal.Instant.fromEpochSeconds(value)
                            .toZonedDateTimeISO(Temporal.Now.timeZoneId())
                            .toPlainDate()
                            .toString();
                    }
                    return value;
                })();

                return (
                    <FieldContainer
                        errorId={errorId}
                        errors={errors}
                        descriptionId={descriptionId}
                        description={description}
                    >
                        <Input
                            {...inputProps}
                            className={classNames(styles.dateInput, className)}
                            id={fieldId}
                            type="date"
                            name={name}
                            label={label}
                            value={currentValue}
                            onChange={handleOnChange}
                            onBlur={onBlur}
                            aria-invalid={!isValid}
                            aria-errormessage={errorId}
                            aria-describedby={descriptionId}
                            required={required}
                            requiredStrategy={requiredStrategy}
                        />
                    </FieldContainer>
                );
            }}
        </Field>
    );
};
